import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  AutopilotSearchStrategyJobEnum,
  CuratedSearchDesignTabContainer_CreateInternalAutopilotFiltersMutation,
  CuratedSearchDesignTabContainer_CreateInternalAutopilotFiltersMutationVariables,
  CuratedSearchDesignTabContainer_OrgContactInternalSearchPreviewMutation,
  CuratedSearchDesignTabContainer_OrgContactInternalSearchPreviewMutationVariables,
  CuratedSearchDesignTabContainer_SearchAnalysisPreviewMutation,
  CuratedSearchDesignTabContainer_SearchAnalysisPreviewMutationVariables,
  CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearchMutation,
  CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearchMutationVariables,
  OpensearchCuratedSearch,
  OrgContactInternalSearchPreview,
  PreviousResultsInput,
  RemixContextInput,
  SearchAnalysisPreviewResults,
} from "../../__generated__/graphql";
import {
  CuratedSearchDesignTabContainer_CreateInternalAutopilotFilters,
  CuratedSearchDesignTabContainer_OrgContactInternalSearchPreview,
  CuratedSearchDesignTabContainer_SearchAnalysisPreview,
  CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearch,
} from "../../components/autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignTabContainer";
import { ICuratedSearchDesignTabStatus } from "../../components/autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignTab";
import { CuratedSearchDesignTabStatusEnum } from "../../components/autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignTabsContainer";
import toast from "react-hot-toast";
import { removeTypename } from "../../utils/formatters";
import {
  IPreviousResultsWithFeedback,
  PreviewContactFeedbackMap,
} from "../../contexts/InternalAutopilotHydrationContext";

export interface IAutopilotSearchDesignRemixContext {
  remixPrompt: string;
  previousInternalSearchFilters: OpensearchCuratedSearch | null;
  previousResults?: IPreviousResultsWithFeedback[] | null;
}

interface UseCuratedSearchDesignStateMachineProps {
  strategy: AutopilotSearchStrategyJobEnum;
  prompt: string;
  tabStatus: ICuratedSearchDesignTabStatus;
  setTabStatus: (tabStatus: ICuratedSearchDesignTabStatus) => void;
  setPreviewContactFeedback: (
    previewContactFeedback: PreviewContactFeedbackMap,
  ) => void;
  scoringCriteriaValues: string[];
  remixContext?: RemixContextInput | null;
}

const useCuratedSearchDesignStateMachine = ({
  strategy,
  prompt,
  tabStatus,
  setTabStatus,
  setPreviewContactFeedback,
  scoringCriteriaValues,
  remixContext,
}: UseCuratedSearchDesignStateMachineProps) => {
  const [curatedSearchId, setCuratedSearchId] = useState<number | null>(null);
  const [localScoringCriteriaValues, setLocalScoringCriteriaValues] = useState<
    string[]
  >(scoringCriteriaValues);

  // Mutations
  const [createInternalAutopilotFilters] = useMutation<
    CuratedSearchDesignTabContainer_CreateInternalAutopilotFiltersMutation,
    CuratedSearchDesignTabContainer_CreateInternalAutopilotFiltersMutationVariables
  >(CuratedSearchDesignTabContainer_CreateInternalAutopilotFilters);
  const [orgContactInternalSearchPreview] = useMutation<
    CuratedSearchDesignTabContainer_OrgContactInternalSearchPreviewMutation,
    CuratedSearchDesignTabContainer_OrgContactInternalSearchPreviewMutationVariables
  >(CuratedSearchDesignTabContainer_OrgContactInternalSearchPreview);
  const [searchAnalysisPreview] = useMutation<
    CuratedSearchDesignTabContainer_SearchAnalysisPreviewMutation,
    CuratedSearchDesignTabContainer_SearchAnalysisPreviewMutationVariables
  >(CuratedSearchDesignTabContainer_SearchAnalysisPreview);
  const [upsertAutopilotCuratedSearch] = useMutation<
    CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearchMutation,
    CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearchMutationVariables
  >(CuratedSearchDesignTabContainer_UpsertAutopilotCuratedSearch);

  useEffect(() => {
    if (
      JSON.stringify(localScoringCriteriaValues) ===
      JSON.stringify(scoringCriteriaValues)
    ) {
      return;
    }

    setLocalScoringCriteriaValues(scoringCriteriaValues);

    if (
      tabStatus.state === CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS ||
      tabStatus.state === CuratedSearchDesignTabStatusEnum.COMPLETED
    ) {
      setTabStatus({
        state: CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS,
        savedFilters: tabStatus.savedFilters,
        savedPreviews: tabStatus.savedPreviews,
      });
    }
  }, [
    scoringCriteriaValues,
    tabStatus,
    setTabStatus,
    localScoringCriteriaValues,
  ]);

  // Main state machine useEffect - drives all logic based on tabStatus.state
  useEffect(() => {
    const currentState = tabStatus.state;

    switch (currentState) {
      case CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH:
        if (!tabStatus.savedFilters?.opensearchFiltersBlob) {
          setTabStatus({
            state: CuratedSearchDesignTabStatusEnum.LOADING_FILTERS,
            savedFilters: tabStatus.savedFilters,
          });
        } else {
          // When we have initial filters, we need to create/get a curated search ID
          upsertAutopilotCuratedSearch({
            variables: {
              input: {
                promptText: prompt,
                internalSearchFilters: {
                  auxiliary_filters: {
                    companyNames:
                      tabStatus.savedFilters.auxiliary_filters?.companyNames,
                    orgCompanyIds:
                      tabStatus.savedFilters.auxiliary_filters?.orgCompanyIds,
                  },
                  opensearchFiltersBlob:
                    tabStatus.savedFilters.opensearchFiltersBlob,
                },
                scoringCriteria: [],
              },
            },
            onCompleted: (data) => {
              if (
                data.upsertAutopilotCuratedSearch.success &&
                data.upsertAutopilotCuratedSearch.autopilotCuratedSearch
              ) {
                setCuratedSearchId(
                  data.upsertAutopilotCuratedSearch.autopilotCuratedSearch.id,
                );
                if (
                  tabStatus.savedAnalysisPreview &&
                  tabStatus.savedPreviews &&
                  tabStatus.savedFilters
                ) {
                  const relevantContactCount =
                    tabStatus.savedAnalysisPreview.analysisResults.filter(
                      (result: any) =>
                        (result.scoringCriteriaResults?.filter(
                          (criteria: any) => criteria.criterion_fit,
                        ).length ?? 0) >=
                        (result.scoringCriteriaResults?.length ?? 0) / 2,
                    ).length ?? 0;

                  setTabStatus({
                    state: CuratedSearchDesignTabStatusEnum.COMPLETED,
                    savedFilters: tabStatus.savedFilters,
                    savedPreviews: tabStatus.savedPreviews,
                    savedAnalysisPreview: tabStatus.savedAnalysisPreview,
                    totalContact: tabStatus.savedPreviews.totalResultCount,
                    relevantContact: relevantContactCount,
                  });
                } else if (tabStatus.savedPreviews && tabStatus.savedFilters) {
                  setTabStatus({
                    state: CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS,
                    savedFilters: tabStatus.savedFilters,
                    savedPreviews: tabStatus.savedPreviews,
                  });
                } else if (tabStatus.savedFilters) {
                  setTabStatus({
                    state: CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS,
                    savedFilters: tabStatus.savedFilters,
                  });
                } else {
                  setTabStatus({
                    state: CuratedSearchDesignTabStatusEnum.LOADING_FILTERS,
                    savedFilters: tabStatus.savedFilters,
                  });
                }
              } else {
                toast.error("Failed to initialize from existing search");
              }
            },
          });
        }
        break;

      case CuratedSearchDesignTabStatusEnum.LOADING_FILTERS:
        createInternalAutopilotFilters({
          variables: {
            input: {
              prompt,
              strategy,
              autopilotCuratedSearchId: curatedSearchId,
              specificCompanies:
                tabStatus.savedFilters?.auxiliary_filters?.companyNames || [],
              remixContext: remixContext ?? undefined,
            },
          },
          onCompleted: (data) => {
            const newFilters =
              data.createInternalAutopilotFilters.internalCuratedSearch;
            setCuratedSearchId(data.createInternalAutopilotFilters.id || null);
            if (newFilters) {
              setTabStatus({
                state: CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS,
                savedFilters: newFilters,
              });
            } else {
              // Handle error case - filters creation failed
              toast.error("Failed to create filters");
            }
          },
        });
        break;

      case CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS:
        orgContactInternalSearchPreview({
          variables: {
            input: {
              curatedSearchId: curatedSearchId!,
              opensearchCuratedSearch: {
                opensearchFiltersBlob:
                  tabStatus.savedFilters.opensearchFiltersBlob,
                auxiliary_filters: {
                  companyNames:
                    tabStatus.savedFilters.auxiliary_filters?.companyNames ||
                    [],
                  orgCompanyIds:
                    tabStatus.savedFilters.auxiliary_filters?.orgCompanyIds ||
                    [],
                },
              },
            },
          },
          onCompleted: (data) => {
            const newPreviews = data.orgContactInternalSearchPreview;
            setPreviewContactFeedback(new Map<number, PreviousResultsInput>());
            if (newPreviews) {
              setTabStatus({
                state: CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS,
                savedFilters: tabStatus.savedFilters,
                savedPreviews: newPreviews as OrgContactInternalSearchPreview,
              });
            } else {
              toast.error("Failed to fetch contact previews");
            }
          },
        });
        break;

      case CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS:
        const analyzeContacts = () => {
          searchAnalysisPreview({
            variables: {
              input: {
                curatedSearchId: curatedSearchId!,
                orgContactIds: tabStatus.savedPreviews.orgContacts.map(
                  (orgContact: any) => orgContact.id!,
                ),
              },
            },
            onCompleted: (data) => {
              const newSearchAnalysis = data.searchAnalysisPreview;

              if (
                newSearchAnalysis &&
                tabStatus.savedFilters &&
                tabStatus.savedPreviews
              ) {
                // Calculate relevant contact count for completed status
                const newRelevantContactCount =
                  newSearchAnalysis.analysisResults.filter(
                    (result: any) =>
                      (result.scoringCriteriaResults?.filter(
                        (criteria: any) => criteria.criterion_fit,
                      ).length ?? 0) >=
                      (result.scoringCriteriaResults?.length ?? 0) / 2,
                  ).length ?? 0;

                setTabStatus({
                  state: CuratedSearchDesignTabStatusEnum.COMPLETED,
                  savedFilters: tabStatus.savedFilters,
                  savedPreviews: tabStatus.savedPreviews,
                  savedAnalysisPreview:
                    newSearchAnalysis as SearchAnalysisPreviewResults,
                  totalContact: tabStatus.savedPreviews.totalResultCount,
                  relevantContact: newRelevantContactCount,
                });
              } else {
                toast.error("Failed to analyze contacts");
              }
            },
          });
        };

        upsertAutopilotCuratedSearch({
          variables: {
            input: {
              id: curatedSearchId!,
              scoringCriteria: scoringCriteriaValues.map((c) => ({
                criterion: c,
                required: false,
                extra_detail: undefined,
              })),
              promptText: prompt,
              internalSearchFilters: removeTypename(tabStatus.savedFilters),
              totalResultCount: tabStatus.savedPreviews?.totalResultCount,
            },
          },
          onCompleted: () => {
            analyzeContacts();
          },
        });
        break;

      case CuratedSearchDesignTabStatusEnum.COMPLETED:
        break;
    }
    // State machine only updates when tabStatus changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabStatus]);

  // Return state values that might be useful to parent
  const tabStatusSavedFilters =
    "savedFilters" in tabStatus ? tabStatus.savedFilters : null;
  const tabStatusSavedPreviews =
    "savedPreviews" in tabStatus ? tabStatus.savedPreviews : null;
  const tabStatusSavedAnalysisPreview =
    "savedAnalysisPreview" in tabStatus ? tabStatus.savedAnalysisPreview : null;

  return {
    filters: tabStatusSavedFilters,
    previews: tabStatusSavedPreviews,
    searchAnalysis: tabStatusSavedAnalysisPreview,
    curatedSearchId,
    currentState: tabStatus.state,
  };
};

export default useCuratedSearchDesignStateMachine;
